import React from 'react';
import Head from 'next/head';

import { route } from '@edx/helpers/url';
import { StaticPage } from '@edx/components/static-page';
import { useAppConfigStateContext } from '@edx/contexts/app-config';

type Props = {
  url: string;
  email: string;
};

export const Legal: React.FC<Props> = ({ url, email }) => {
  const { projectName } = useAppConfigStateContext();
  const entityName = `${projectName.toUpperCase()} PTE. LTD.`;

  return (
    <>
      <Head>
        <title key="title">Términos y condiciones</title>

        <link rel="canonical" href={route('/aviso-legal')} />
      </Head>

      <StaticPage title="Términos y condiciones">
        <p>Actualizado por última vez el: 1 de Marzo de 2019</p>
        <p>
          Estos Términos y condiciones ("Términos", "Términos y condiciones") rigen nuestra relación con la página web
          {url} (el "Servicio"), operada por {entityName} ("nosotros" o "nuestro").
        </p>
        <p>Por favor, lea con atención los siguientes Términos y Condiciones antes de utilizar el Servicio.</p>
        <p>
          Su acceso al Servicio y su utilización está condicionada por su aceptación y cumplimiento de los siguientes
          Términos, aplicables a todos los visitantes, usuarios y toda aquella persona que acceda o utilice el Servicio.
        </p>
        <p>
          Al acceder o utilizar el Servicio, usted está obligado legalmente al cumplimiento de estos Términos. Si no
          está de acuerdo con alguna de sus partes, le aconsejamos no acceder al Servicio.
        </p>

        <h3>Suscripciones</h3>

        <p>
          Algunas partes del Servicio se facturan a través de suscripciones ("Suscripción/es"). En esos casos, se le
          facturará por adelantado de forma recurrente y periódica ("Ciclo de facturación"). Los Ciclos de facturación
          pueden ser mensuales, trimestrales o anuales, dependiendo del tipo de plan de suscripción que haya
          seleccionado al abonarse.
        </p>
        <p>
          Al final de cada Ciclo de facturación, su Suscripción se renovará automáticamente bajo las mismas condiciones
          a menos que la cancele o que {entityName} la cancele. Puede cancelar la renovación de su Suscripción bien a
          través de la página de gestión de su cuenta online, o bien contactando con el servicio de atención al cliente
          de {entityName}.
        </p>
        <p>
          Para procesar el abono de su Suscripción se requiere el uso de un método de pago válido, entre los que se
          incluye el pago con tarjeta de crédito. Deberá proporcionar unos datos de facturación correctos y completos a
          {entityName}, como su nombre completo, dirección, Estado, código postal, número de teléfono y datos de pago
          válidos. Al facilitar dicha información de pago, usted estará autorizando automáticamente que ESCUELA DE
          BAJISTAS PTE. LTD. le facture todas las cuotas de suscripción incurridas a través de su cuenta por medio de
          esos métodos de pago.
        </p>
        <p>
          Si la facturación automática no se produjese por algún motivo, {entityName} emitirá una factura electrónica en
          la que indicará que usted debe realizar este procedimiento manualmente dentro de unos plazos determinados y en
          la que se especificará el pago correspondiente a ese Ciclo de facturación.
        </p>
        <h3>Modificaciones de precios</h3>
        <p>
          {entityName} puede, según su criterio y en cualquier momento, modificar las cuotas de Suscripción. Cualquier
          cambio de las cuotas de Suscripción entrará en vigor al final del Ciclo de facturación actual.
        </p>
        <p>
          {entityName} le notificará con la suficiente antelación de cualquier modificación en las cuotas de Suscripción
          para darle la oportunidad de finalizar su Suscripción antes de que dichas modificaciones se hagan efectivas.
        </p>
        <p>
          Un uso continuado del Servicio tras la entrada en vigor de la cuota de Suscripción modificada supone una
          aceptación implícita del abono de dicha cantidad.
        </p>

        <h3>Reembolsos</h3>

        <p>
          {entityName} puede evaluar ciertas solicitudes de reembolso individualmente y basándose únicamente en el
          criterio de {entityName}.
        </p>

        <h3>Contenido</h3>

        <p>
          Nuestro Servicio le permite publicar, enlazar, almacenar, compartir y, en cualquier caso, hacer disponible
          cierta información, texto, material gráfico, vídeos u otro material ("Contenido"). Usted es responsable del
          Contenido que publique en el Servicio, incluyendo su legalidad, fiabilidad y pertinencia.
        </p>
        <p>
          Al publicar Contenido en el Servicio, usted nos está concediendo el derecho a utilizarlo, modificarlo,
          utilizarlo y mostrarlo públicamente, reproducirlo y distribuirlo en y por el Servicio. Usted retendrá todos
          los derechos respecto al Contenido que envíe, publique o muestre en y por el Servicio, y será el responsable
          de proteger dichos derechos. A través de esta licencia, usted se muestra de acuerdo con cedernos el derecho a
          poner su Contenido a disposición de otros usuarios del Servicio, que tendrán la posibilidad de utilizarlo
          estando siempre sujetos Términos.
        </p>
        <p>
          Usted asegura que: (i) el Contenido es suyo (es de su propiedad) o que tiene derecho a usarlo, que nos cede
          sus derechos y su autorización del modo estipulado en estos Términos, y que (ii) la publicación de su
          Contenido en o por el Servicio no viola el derecho a la privacidad, la publicidad o la autoría, los derechos
          contractuales ni cualquier otro derecho de otras personas.
        </p>

        <h3>Cuentas</h3>

        <p>
          Cuando cree una cuenta con nosotros, deberá proporcionarnos información exacta, completa y actual en todo
          momento. En caso contrario estaría violando estos Términos, lo que podría conducir al cierre inmediato de su
          cuenta en nuestro Servicio.
        </p>
        <p>
          Usted es el responsable de salvaguardar su contraseña de acceso al Servicio y a cualquiera de las actividades
          o acciones que lo requieran, independientemente de que la contraseña corresponda a nuestro Servicio o a un
          servicio proporcionado por terceros.
        </p>
        <p>
          A través de estos términos, usted se responsabiliza a no revelar su contraseña a terceros. Si se produce
          cualquier violación de la seguridad del sitio o un uso no autorizado de su cuenta, deberá informarnos de
          inmediato.
        </p>
        <p>
          Está prohibida la utilización de un nombre de usuario que contenga el nombre de otra persona u entidad no
          disponible legalmente; el uso de un nombre o marca registrada que esté sujeta a unos derechos sobre los que el
          usuario no tenga autorización; o la utilización de nombres ofensivos, vulgares u obscenos. El equipo de
          escueladebajistas.com se reserva el derecho a editar cualquier parámetro de su cuenta para adecuarlo a las
          buenas prácticas.
        </p>

        <h3>Política de derechos de autor</h3>

        <p>
          Nuestra política se basa en el respeto a los derechos de propiedad intelectual de los demás. Responderemos a
          cualquier reclamación relacionada con la publicación en el Servicio de Contenidos que supongan la violación
          del copyright o de otros derechos de propiedad intelectual ("Violación") de cualquier persona.
        </p>
        <p>
          Si usted es propietario de un derecho de propiedad intelectual o un representante autorizado de dicho
          propietario y cree que su trabajo con derechos reservados ha sido copiado violando la propiedad intelectual en
          nuestro Servicio, deberá notificarnos por escrito enviando un e-mail a {email}, indicando "Violación de
          copyright" en el asunto e incluyendo una descripción detallada de la supuesta violación de derechos.
        </p>
        <p>
          Es posible que se le pida que rinda cuentas por los daños (incluyendo costes y honorarios de abogados) por
          malinterpretar que ese Contenido haya violado el copyright.
        </p>

        <h3>Propiedad intelectual</h3>

        <p>
          El Servicio y sus contenidos originales (excluyendo el Contenido aportado por los usuarios), características y
          funcionalidades son y seguirán siendo de la propiedad exclusiva de {entityName} y de los titulares de la
          licencia. El Servicio está protegido por los derechos de propiedad intelectual, de marca registrada y por
          otras normas, tanto de Singapore como de países extranjeros. Nuestras marcas registradas y signos distintivos
          no pueden utilizarse en otros productos o servicios sin antes haber obtenido la autorización escrita expresa
          de {entityName}.
        </p>

        <h3>Enlaces a otras páginas web</h3>

        <p>
          Nuestro Servicio puede contener enlaces a páginas web o servicios de terceros que no están controlados ni son
          propiedad de {entityName}.
        </p>
        <p>
          {entityName} no asume ninguna responsabilidad ni goza de ningún tipo de control sobre el contenido, políticas
          de privacidad o prácticas de cualquier página web o servicio dependiente de terceros. A través de estos
          términos, usted está aceptando y reconociendo que {entityName} no debería hacerse responsable, tanto directa
          como indirectamente, de cualquier daño o pérdida causada o presuntamente causada por o en relación con el uso
          o la confianza depositada en tales contenidos, bienes o servicios disponibles a través de esas páginas webs o
          servicios.
        </p>
        <p>
          Le recomendamos encarecidamente que lea los términos y condiciones y las políticas de privacidad de cualquier
          servicio o página web dependiente de terceros que visite.
        </p>

        <h3>Cierre</h3>

        <p>
          Existe la posibilidad de que tomemos la decisión de cerrar o suspender su cuenta de manera inmediata y por
          cualquier razón (entre ellas, por violar de estos Términos), sin necesidad de notificación previa y sin asumir
          responsabilidades de ningún tipo.
        </p>
        <p>
          Tras el cierre, se suspenderá de manera inmediata su derecho a acceder al Servicio. Si desea cerrar su cuenta,
          simplemente puede dejar de utilizar el Servicio.
        </p>

        <h3>Limitación de responsabilidad</h3>

        <p>
          Ni {entityName} ni sus directivos, empleados, socios, agentes, distribuidores o afiliados serán responsables,
          bajo ninguna circunstancia, de cualquier daño indirecto, accidental, especial, causal o punitivo, entre los
          que se incluye la pérdida de beneficios, información, utilidad, benevolencia y otras pérdidas intangibles que
          se hayan producido a causa de (i) su acceso, uso o incapacidad de acceder o utilizar el Servicio; (ii)
          cualquier comportamiento o contenido aportado por un tercero al Servicio; (iii) cualquier contenido obtenido
          del Servicio; y (iv) el acceso no autorizado, la utilización o modificación de sus emisiones o contenidos,
          aunque estén soportados por una garantía, contrato, agravio (negligencias incluidas) o cualquier otra teoría
          legal, e independientemente de que se les haya informado o no de la posibilidad de que se produzcan tales
          daños, incluso si se demuestra que los remedios provistos de antemano aquí no han conseguido su objetivo
          principal.
        </p>

        <h3>Descargo de responsabilidad</h3>

        <p>
          Su uso del Servicio está bajo su propia responsabilidad. El Servicio se proporciona tal como se encuentre/esté
          disponible, y sin garantías de cualquier tipo, sean éstas expresas o implícitas, e incluyendo, entre otras,
          las garantías implícitas de comercialización, adecuación para un propósito particular, no vulneración o rumbo
          del rendimiento.
        </p>
        <p>
          {entityName}, sus filiales, afiliados y licenciadores no garantizan que a) el Servicio vaya a funcionar de
          forma ininterrumpida y segura, o que vaya a estar disponible en cualquier momento y desde cualquier ubicación;
          b) que se vayan a corregir sus errores o defectos; c) que el Servicio esté libre de virus u otros componentes
          dañinos; o que d) los resultados de utilizar el Servicio estarán a la altura de sus expectativas.
        </p>

        <h3>Ley vigente</h3>

        <p>
          Estos Términos deberán regirse e interpretarse según las leyes de Singapore, sin tener en cuenta el conflicto
          con sus provisiones legales.
        </p>
        <p>
          Nuestra incapacidad para hacer cumplir cualquier derecho o provisión presente en estos Términos no se
          considerará una exención de dichos derechos. Si alguna provisión de estos Términos es invalidada o no puede
          ser ejecutada por un tribunal, las provisiones restantes de estos Términos seguirán vigentes. Estos Términos
          constituyen el acuerdo completo en relación con el Servicio que ofrecemos, y por tanto reemplazan cualquier
          acuerdo previo en relación con el Servicio.
        </p>

        <h3>Cambios</h3>

        <p>
          Nos reservamos el derecho a modificar o reemplazar estos Términos en cualquier momento y según nuestro propio
          criterio. Si se produce una revisión material, haremos un esfuerzo razonable por notificar con al menos 60
          días de antelación a la entrada en vigor de los nuevos términos. Determinaremos qué constituye una
          modificación material siguiendo nuestro criterio.
        </p>
        <p>
          Al continuar accediendo o utilizando nuestro Servicio después de que esos cambios tengan efecto, usted está
          accediendo a cumplir los términos revisados. Si no está de acuerdo con los nuevos términos, tanto de forma
          parcial como total, le rogamos que deje de utilizar la página web y el Servicio.
        </p>

        <h3>Contacte con nosotros</h3>

        <p>
          Si tiene alguna pregunta sobre esta Política de privacidad, contacte con nosotros:{' '}
          <a href={`mailto:${email}`}>{email}</a>
        </p>
      </StaticPage>
    </>
  );
};
